import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import Sidebar from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription 
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Plus, 
  Edit,
  Trash2,
  Copy,
  ExternalLink,
  Check,
  X,
  Users,
  Link2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IntakeFormsPage = () => {
  const { token } = useAuth();
  const [forms, setForms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  
  // Form state
  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formTitles, setFormTitles] = useState(['']);
  const [formGroupId, setFormGroupId] = useState('');

  const fetchForms = useCallback(async () => {
    try {
      const response = await fetch(`${API}/intake-forms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForms(data);
      }
    } catch (error) {
      console.error('Error fetching intake forms:', error);
      toast.error('Failed to load intake forms');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await fetch(`${API}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter groups that have members
        setGroups(data.filter(g => g.member_count > 0));
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchForms();
    fetchGroups();
  }, [fetchForms, fetchGroups]);

  const resetForm = () => {
    setFormName('');
    setFormSlug('');
    setFormTitles(['']);
    setFormGroupId('');
    setSelectedForm(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (form) => {
    setSelectedForm(form);
    setFormName(form.name);
    setFormSlug(form.slug);
    setFormTitles(form.titles.length > 0 ? form.titles : ['']);
    setFormGroupId(form.assigned_group_id);
    setShowEditModal(true);
  };

  // Auto-generate slug from name
  const handleNameChange = (value) => {
    setFormName(value);
    if (!showEditModal) {
      // Only auto-generate for new forms
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormSlug(slug);
    }
  };

  const addTitleOption = () => {
    setFormTitles([...formTitles, '']);
  };

  const removeTitleOption = (index) => {
    if (formTitles.length > 1) {
      setFormTitles(formTitles.filter((_, i) => i !== index));
    }
  };

  const updateTitleOption = (index, value) => {
    const newTitles = [...formTitles];
    newTitles[index] = value;
    setFormTitles(newTitles);
  };

  const handleCreateForm = async () => {
    // Validation
    if (!formName.trim()) {
      toast.error('Please enter a form name');
      return;
    }
    if (!formSlug.trim()) {
      toast.error('Please enter a URL slug');
      return;
    }
    if (!formGroupId) {
      toast.error('Please select a group to assign tasks to');
      return;
    }
    const validTitles = formTitles.filter(t => t.trim());
    if (validTitles.length === 0) {
      toast.error('Please add at least one title option');
      return;
    }

    try {
      const response = await fetch(`${API}/intake-forms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formName.trim(),
          slug: formSlug.trim(),
          titles: validTitles,
          assigned_group_id: formGroupId
        })
      });

      if (response.ok) {
        toast.success('Intake form created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchForms();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create intake form');
      }
    } catch (error) {
      toast.error('Failed to create intake form');
    }
  };

  const handleUpdateForm = async () => {
    if (!formName.trim()) {
      toast.error('Please enter a form name');
      return;
    }
    if (!formSlug.trim()) {
      toast.error('Please enter a URL slug');
      return;
    }
    if (!formGroupId) {
      toast.error('Please select a group to assign tasks to');
      return;
    }
    const validTitles = formTitles.filter(t => t.trim());
    if (validTitles.length === 0) {
      toast.error('Please add at least one title option');
      return;
    }

    try {
      const response = await fetch(`${API}/intake-forms/${selectedForm.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formName.trim(),
          slug: formSlug.trim(),
          titles: validTitles,
          assigned_group_id: formGroupId
        })
      });

      if (response.ok) {
        toast.success('Intake form updated successfully!');
        setShowEditModal(false);
        resetForm();
        fetchForms();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update intake form');
      }
    } catch (error) {
      toast.error('Failed to update intake form');
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this intake form?')) {
      return;
    }

    try {
      const response = await fetch(`${API}/intake-forms/${formId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Intake form deleted successfully!');
        fetchForms();
      } else {
        toast.error('Failed to delete intake form');
      }
    } catch (error) {
      toast.error('Failed to delete intake form');
    }
  };

  const handleToggleActive = async (form) => {
    try {
      const response = await fetch(`${API}/intake-forms/${form.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          is_active: !form.is_active
        })
      });

      if (response.ok) {
        toast.success(`Form ${form.is_active ? 'deactivated' : 'activated'} successfully!`);
        fetchForms();
      } else {
        toast.error('Failed to update form status');
      }
    } catch (error) {
      toast.error('Failed to update form status');
    }
  };

  const copyPublicUrl = (form) => {
    if (form.public_url) {
      navigator.clipboard.writeText(form.public_url);
      setCopiedId(form.id);
      toast.success('URL copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const renderFormContent = () => (
    <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
      {/* Form Name */}
      <div className="space-y-2">
        <Label htmlFor="form-name">Form Name *</Label>
        <Input
          id="form-name"
          value={formName}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="e.g., Customer Support Request"
          data-testid="intake-form-name-input"
          autoComplete="off"
        />
      </div>
      
      {/* URL Slug */}
      <div className="space-y-2">
        <Label htmlFor="form-slug">URL Slug *</Label>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">/intake/</span>
          <Input
            id="form-slug"
            value={formSlug}
            onChange={(e) => setFormSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="customer-support"
            className="flex-1"
            data-testid="intake-form-slug-input"
            autoComplete="off"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          This will be part of the public URL. Use only lowercase letters, numbers, and hyphens.
        </p>
      </div>
      
      {/* Assign to Group */}
      <div className="space-y-2">
        <Label>Assign Submissions To *</Label>
        <Select value={formGroupId} onValueChange={setFormGroupId}>
          <SelectTrigger data-testid="intake-form-group-select">
            <SelectValue placeholder="Select a group" />
          </SelectTrigger>
          <SelectContent>
            {groups.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground text-center">
                No groups with members available
              </div>
            ) : (
              groups.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>{group.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({group.member_count} members)
                    </span>
                  </div>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          All members of this group will receive submitted tasks.
        </p>
      </div>
      
      {/* Title Options */}
      <div className="space-y-2">
        <Label>Title Options (Dropdown) *</Label>
        <p className="text-xs text-muted-foreground mb-2">
          Users will select one of these titles when submitting the form.
        </p>
        <div className="space-y-2">
          {formTitles.map((title, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={title}
                onChange={(e) => updateTitleOption(index, e.target.value)}
                placeholder={`Option ${index + 1} (e.g., Bug Report)`}
                data-testid={`intake-form-title-${index}`}
                autoComplete="off"
              />
              {formTitles.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={() => removeTitleOption(index)}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addTitleOption}
          className="mt-2"
          data-testid="add-title-option-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Option
        </Button>
      </div>
    </div>
  );

  return (
    <Sidebar>
      <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold">Intake Forms</h1>
            <p className="text-muted-foreground mt-1">
              Create public forms for non-registered users to submit tasks
            </p>
          </div>
          <Button onClick={openCreateModal} data-testid="create-intake-form-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Intake Form
          </Button>
        </div>

        {/* Forms List */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Your Intake Forms
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading forms...</div>
            ) : forms.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No intake forms yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Create a form to receive submissions from non-registered users
                </p>
                <Button onClick={openCreateModal} className="mt-4" variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Form
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {forms.map((form) => (
                  <div
                    key={form.id}
                    className="p-4 rounded-xl border bg-card transition-all duration-200 hover:bg-muted/50 hover:shadow-md"
                    data-testid={`intake-form-card-${form.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <h4 className="font-medium text-lg">{form.name}</h4>
                          <Badge variant={form.is_active ? "default" : "secondary"}>
                            {form.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Link2 className="w-4 h-4 shrink-0" />
                            <span className="truncate">/intake/{form.slug}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 shrink-0" />
                            <span>Assigned to: {form.assigned_group_name}</span>
                          </div>
                        </div>
                        
                        <div className="mt-3 flex flex-wrap gap-1">
                          {form.titles.slice(0, 4).map((title, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {title}
                            </Badge>
                          ))}
                          {form.titles.length > 4 && (
                            <Badge variant="outline" className="text-xs">
                              +{form.titles.length - 4} more
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 shrink-0">
                        {/* Active Toggle */}
                        <div className="flex items-center gap-2 mr-2">
                          <Switch
                            checked={form.is_active}
                            onCheckedChange={() => handleToggleActive(form)}
                            data-testid={`toggle-form-${form.id}`}
                          />
                        </div>
                        
                        {/* Copy URL */}
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => copyPublicUrl(form)}
                          title="Copy public URL"
                          data-testid={`copy-url-${form.id}`}
                        >
                          {copiedId === form.id ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        
                        {/* Open Form */}
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => window.open(`/intake/${form.slug}`, '_blank')}
                          title="Open public form"
                          data-testid={`open-form-${form.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        
                        {/* Edit */}
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => openEditModal(form)}
                          title="Edit form"
                          data-testid={`edit-form-${form.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        
                        {/* Delete */}
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteForm(form.id)}
                          title="Delete form"
                          data-testid={`delete-form-${form.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modals */}
        {/* Create Modal */}
        <Dialog 
          open={showCreateModal} 
          onOpenChange={(open) => {
            if (!open) {
              setShowCreateModal(false);
              resetForm();
            }
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Create New Intake Form
              </DialogTitle>
              <DialogDescription>
                Create a public form for non-registered users to submit tasks.
              </DialogDescription>
            </DialogHeader>
            {renderFormContent()}
            <DialogFooter className="pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateForm}
                data-testid="save-intake-form-btn"
              >
                Create Form
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog 
          open={showEditModal} 
          onOpenChange={(open) => {
            if (!open) {
              setShowEditModal(false);
              resetForm();
            }
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Edit Intake Form
              </DialogTitle>
              <DialogDescription>
                Update the intake form settings below.
              </DialogDescription>
            </DialogHeader>
            {renderFormContent()}
            <DialogFooter className="pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateForm}
                data-testid="save-intake-form-btn"
              >
                Update Form
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Sidebar>
  );
};

export default IntakeFormsPage;
